import { useState, useMemo } from 'react';

import { useGetSummaryGraphQuery } from '~entities/summary';
import type { SummaryFilter } from '~entities/summary';

import { formatMoney } from '~shared/lib/format-money';
import { Loader } from '~shared/ui/loader';

import Filter from '@breeze-platform-ui/filter';
import {
  ResponsiveContainer,
  AreaChart,
  XAxis,
  YAxis,
  Tooltip,
  Area,
  CartesianGrid,
} from 'recharts';

import { renderTooltip } from './tooltip';

import { summaryFieldOptions, isMoneyType } from '../lib';
import type { SummaryDataField } from '../types';

type TotalAmountInfo = {
  [key in SummaryDataField]: string;
};
type Props = {
  filters: SummaryFilter;
};

export const SummaryGraph = ({ filters }: Props) => {
  const [dataField, setDataField] = useState<SummaryDataField>('loansAmount');

  const { data, isLoading, isFetching, isPreviousData } =
    useGetSummaryGraphQuery(filters);

  const totalAmount: TotalAmountInfo | undefined = useMemo(() => {
    if (data) {
      const total = data.reduce(
        (acc, cur) => ({
          loansAmount: acc.loansAmount + cur.loansAmount,
          salesAmount: acc.salesAmount + cur.salesAmount,
          loansCount: acc.loansCount + cur.loansCount,
        }),
        { loansAmount: 0, salesAmount: 0, loansCount: 0 }
      );
      return {
        loansAmount: formatMoney(total.loansAmount),
        salesAmount: formatMoney(total.salesAmount),
        loansCount: total.loansCount.toString(),
      };
    }
    return undefined;
  }, [data]);

  const formatDate = (value: string, type: 'short' | 'long') =>
    new Date(value).toLocaleDateString('en', {
      month: type,
      day: 'numeric',
    });

  if (isLoading) {
    return (
      <div className="relative flex h-[620px] flex-col">
        <Loader />
      </div>
    );
  }
  if (!data?.length) {
    return (
      <div
        className={`
          flex h-[500px] justify-center pt-20 text-center text-[17px]
          text-gray-500
        `}
      >
        We didn&rsquo;t find anything
      </div>
    );
  }

  return (
    <div className="relative flex min-h-[620px] flex-col">
      <Filter
        name="dataField"
        options={summaryFieldOptions}
        value={dataField}
        onChange={(e) => setDataField(e.target.value as SummaryDataField)}
      />
      {totalAmount && (
        <div className="pt-[32px]">
          <p className="mb-[8px] mt-0 text-[17px] text-black/80">
            {formatDate(filters.dateFrom, 'long')} —{' '}
            {formatDate(filters.dateTo, 'long')}
          </p>
          <div className="text-[36px] font-bold tracking-tight">
            {totalAmount[dataField]}
          </div>
        </div>
      )}
      {data && (
        <>
          {isFetching && isPreviousData && <Loader />}
          <ResponsiveContainer
            id="summary-graph-container"
            minWidth="100%"
            minHeight={500}
            height={500}
          >
            <AreaChart
              width={730}
              height={250}
              data={data}
              margin={{ top: 60 }}
            >
              <defs>
                <linearGradient id="dataColor" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#5BA6FD" stopOpacity={0.24} />
                  <stop offset="95%" stopColor="#5BA6FD" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(v) => formatDate(v, 'short')}
              />
              <YAxis
                mirror
                type="number"
                tickLine={false}
                tick={{
                  transform: 'translate(0, -15)',
                }}
                tickFormatter={(v) =>
                  isMoneyType(dataField) ? formatMoney(v) : v
                }
              />

              <Tooltip content={renderTooltip} />
              <Area
                connectNulls
                dataKey={dataField}
                stroke="#5BA6FD"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#dataColor)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
};
